import React, { useMemo, useState } from 'react';
import { brand, BriefStep } from '../../config/brand';
import './Brief.css';

type Status = 'idle' | 'sending' | 'sent' | 'error';

const steps = brand.brief.steps as BriefStep[];
const required = steps.filter((s) => !s.optional);

/** Fire-and-forget GTM event; the container may not be loaded. */
function track(event: string, payload: Record<string, unknown> = {}) {
  const layer = (window as unknown as { dataLayer?: unknown[] }).dataLayer;
  if (Array.isArray(layer)) layer.push({ event, ...payload });
}

/** The answers, rendered as something a human can read in an inbox. */
function composeBrief(answers: Record<string, string>, email: string, context: string) {
  const lines = steps
    .map((s) => {
      const value = answers[s.id];
      return value ? `${s.question}\n  ${value}` : null;
    })
    .filter(Boolean)
    .join('\n\n');

  const body = [
    lines,
    context.trim() ? `Context\n  ${context.trim()}` : null,
    `Reply to\n  ${email.trim()}`,
  ]
    .filter(Boolean)
    .join('\n\n');

  const subject = `Project brief — ${answers.building || 'AI project'} (${answers.timeline || 'timeline TBD'})`;
  return { subject, body };
}

const Brief: React.FC = () => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [context, setContext] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [copied, setCopied] = useState(false);

  const answered = required.filter((s) => answers[s.id]).length;
  const complete = answered === required.length && /\S+@\S+\.\S+/.test(email);
  const progress = useMemo(
    () => Math.round(((answered + (/\S+@\S+\.\S+/.test(email) ? 1 : 0)) / (required.length + 1)) * 100),
    [answered, email]
  );

  const pick = (stepId: string, option: string) =>
    setAnswers((prev) => ({ ...prev, [stepId]: option }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!complete || status === 'sending') return;

    const { subject, body } = composeBrief(answers, email, context);
    track('brief_submit', { ...answers, has_context: Boolean(context.trim()) });

    // With an endpoint configured the visitor never leaves the page.
    if (brand.contact.formEndpoint) {
      setStatus('sending');
      try {
        const res = await fetch(brand.contact.formEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...answers, email, context, subject, message: body }),
        });
        setStatus(res.ok ? 'sent' : 'error');
      } catch {
        setStatus('error');
      }
      return;
    }

    // No endpoint: hand the mail client a brief that is already qualified,
    // so the first reply can be an answer instead of five questions.
    window.location.href = `mailto:${brand.agency.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    setStatus('sent');
  };

  const copyBrief = async () => {
    const { subject, body } = composeBrief(answers, email, context);
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      /* clipboard blocked — the mail draft already has the text */
    }
  };

  if (status === 'sent') {
    return (
      <div className="brief brief--done" role="status">
        <span className="brief__tick" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        </span>
        <h3 className="brief__done-title">
          {brand.contact.formEndpoint ? 'Brief received.' : 'Your brief is ready to send.'}
        </h3>
        <p className="brief__done-sub">
          {brand.contact.formEndpoint
            ? `${brand.contact.responseTime} — from the engineer who'd do the work, not a salesperson.`
            : `We opened a pre-filled email to ${brand.agency.email}. If nothing appeared, copy the brief below and send it however you like.`}
        </p>
        <div className="brief__done-actions">
          <button type="button" className="btn btn-outline btn-sm" onClick={copyBrief}>
            {copied ? 'Copied ✓' : 'Copy the brief'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => { setStatus('idle'); setCopied(false); }}
          >
            Start over
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="brief" onSubmit={submit} noValidate>
      <div className="brief__head">
        <span className="brief__step-count">
          {answered}/{required.length} answered
        </span>
        <span className="brief__rail" aria-hidden="true">
          <span className="brief__rail-fill" style={{ transform: `scaleX(${progress / 100})` }} />
        </span>
      </div>

      {steps.map((step) => (
        <fieldset className="brief__field" key={step.id}>
          <legend className="brief__question">
            {step.question}
            {step.optional && <span className="brief__optional">optional</span>}
          </legend>
          <div className="brief__options">
            {step.options.map((option) => {
              const id = `${step.id}-${option.replace(/\W+/g, '-').toLowerCase()}`;
              return (
                <React.Fragment key={option}>
                  <input
                    className="brief__radio visually-hidden"
                    type="radio"
                    id={id}
                    name={step.id}
                    value={option}
                    checked={answers[step.id] === option}
                    onChange={() => pick(step.id, option)}
                  />
                  <label className="chip brief__chip" htmlFor={id}>
                    <span className="chip__check" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    </span>
                    {option}
                  </label>
                </React.Fragment>
              );
            })}
          </div>
        </fieldset>
      ))}

      <div className="brief__inputs">
        <label className="brief__label" htmlFor="brief-email">
          <span className="brief__label-row">Where should we reply?</span>
          <input
            className="brief__input"
            id="brief-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        <label className="brief__label" htmlFor="brief-context">
          <span className="brief__label-row">
            Anything else? <span className="brief__optional">optional</span>
          </span>
          <textarea
            className="brief__input brief__input--area"
            id="brief-context"
            name="context"
            rows={2}
            placeholder="One line about the system, the stack, or the deadline."
            value={context}
            onChange={(e) => setContext(e.target.value)}
          />
        </label>
      </div>

      {status === 'error' && (
        <p className="brief__error" role="alert">
          That didn't go through. Email us directly at{' '}
          <a href={`mailto:${brand.agency.email}`}>{brand.agency.email}</a>.
        </p>
      )}

      <div className="brief__actions">
        <button
          type="submit"
          className="btn btn-primary brief__submit"
          disabled={!complete || status === 'sending'}
          id="brief-submit"
        >
          {status === 'sending' ? 'Sending…' : 'Send the brief'}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
        </button>

        {brand.contact.callUrl && (
          <a
            className="btn btn-outline"
            href={brand.contact.callUrl}
            target="_blank"
            rel="noopener noreferrer"
            id="brief-book-call"
          >
            Book 20 minutes
          </a>
        )}
      </div>

      <p className="brief__note">
        {brand.contact.responseTime} · No sales sequence, no CRM drip. We read every one of these.
      </p>
    </form>
  );
};

export default Brief;
